import React, {useState, useContext, useEffect, useCallback} from 'react'
import { LocationContext } from '../../context/LocationContext';
import { setLocalStorageEmail, setLocalStorageLoginToken, setLocalStorageJwtToken, setLocalStorageProfilePic } from '../../js/localstorage';
import { loginState } from '../../api/user/login-state';
import { useRouter } from 'next/router';
import { uploadImage } from '../../api/user/post-api';
import { LoadingCover } from '../commons/LoadingCover';
import { SystemError } from '../error/SystemError';
import { loginWithGoogle } from '../../api/user/googleloogin-api';
import axios from 'axios';
import { loginStringsSG } from '../../api/user/login-api';
import { iosLogin } from '../../api/firebase/firebase';
import Link from 'next/link';
import { loginWithApple } from '../../api/user/applelogin-api';
import Script from 'next/script'
import { parseJwt } from '../../service/parse-jwt';
import { googleApiKey, googleCaptchaKey, s3Static } from '../../utils/constants';
import { mobileLogin, validateMobileLoginOTP } from '../../api/mobile/mobile-api';
import { AnimatePresence, motion } from 'framer-motion';
import { openModal } from '../../service/modal-service';
import { CountrySelection } from '../commons/CountrySelection';
import { MARKETS, getRememberedMarket } from '../../utils/markets';
import Image from 'next/image';
import { 
    LoginMethodTabs, 
    MobileNumberForm, 
    OtpVerificationForm, 
    EmailLoginForm, 
    OrDivider,
    AnimatedFormContainer 
} from './SignInForms';

export const NativeAppSignIn = () => {
    const router = useRouter();

    const [ isLoginWithEmail, setIsLoginWithEmail ] = useState(true)
    const [ isGoogleReady, setIsGoogleReady ] = useState(false)
    const [ isGoogleBtnLoading, setIsGoogleBtnLoading ] = useState(true)
    const [ isAppleReady, setAppleReady ] = useState(false);
    const [ isApple, setIsApple ] = useState(false)
    const [ isLoading, setIsLoading ] = useState(false);
    const [ userFullName, setUserFullName ] = useState("");
    const [ userId, setUserId ] = useState("");
    const [ profilePicUrl, setProfilePicUrl ] = useState(undefined);
    const [ isNewFbGoogleUser, setIsNewFbGoogleUser ] = useState(false);
    const [ formValues, setFormValues] = useState({ emailId: "", password: ""});
    const [ passwordType, setPasswordType ] = useState("password");
    const [ isSystemError, setIsSystemError ] = useState(false);
    const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""});    

    const { countryName, countryCd, countryCurrCd, countryTelCd, location, userLatitude, userLongitude } = useContext(LocationContext);    

    // Mobile Login State
    const [ loginTelCd, setLoginTelCd ] = useState("")
    const [ loginCountryCd, setLoginCountryCd ] = useState("")
    const [ mobileNumber, setMobileNumber ] = useState("");
    const [ mobileNumberError, setMobileNumberError ] = useState("Mobile number should be atleast 8 digits");
    const [ isShowOtpForm, SetShowOtpForm ] = useState(false);
    const [ otp, setOtp ] = useState("")
    const [ isShowCountrySelection, setIsShowCountrySelection ] = useState(false)

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues({...formValues, [name]: value});
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        login();
    }

    const callNativeApp = (email) => {
        return new Promise((resolve) => {
            try {
                // Get FCM token injected by iOS WKWebView
                const fcmToken = window.iosFCMToken || '';
                
                // Only call IosLogin if fcmToken is present and non-empty
                if (fcmToken && fcmToken.trim() !== '') {
                    iosLogin(email, fcmToken)
                        .then(() => console.log('IosLogin success'))
                        .catch(err => console.log('IosLogin error:', err));
                }

                // Also notify iOS native code via postMessage
                if (typeof webkit !== 'undefined' && webkit.messageHandlers && webkit.messageHandlers.callbackHandler) {
                    webkit.messageHandlers.callbackHandler.postMessage(email);
                }

                // Allow time for API call and postMessage to complete before navigation
                setTimeout(resolve, 500);
            } catch(err) {
                console.log('callNativeApp error:', err.message);
                resolve();
            }
        });
    }

    const goToHome = useCallback(() => {
        setIsLoading(true)
        router.push("/home")
        .then(() => setIsLoading(false))
    }, [router, setIsLoading])

    const getProfilePicFile = useCallback(async() => {                
        const config = { responseType: 'blob' };
        axios.get(profilePicUrl, config).then( async(response) => {
            let file = response.data;
            try{
                await uploadImage(file, userId, userFullName, "Profile Picture", "Profile", "Public", "strings", "My new Profile Picture");
            } catch {
                setErrorContent({'header': "Error uploading image", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
                setIsSystemError(true);
                return;
            }
            goToHome()            
        }).catch((error) => {
            console.log(error)
            goToHome()
        });
    }, [userId, userFullName, profilePicUrl, goToHome])

    const processUserId = useCallback(() =>{
        if(isNewFbGoogleUser) {
            getProfilePicFile();
        } else {
            goToHome()
        }      
    }, [isNewFbGoogleUser, getProfilePicFile, goToHome])

    const getUserId = async() => {
        try{
            let loginStateResponse = await loginState();    
            setUserId(loginStateResponse.id); 
        } catch {
            setErrorContent({'header': "Cannot get UserId", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            setIsLoading(false);
            return;
        }
    }

    const login = async() => {
        setIsLoading(true);
        let loginResponse;
        try {
            loginResponse = await loginStringsSG(formValues);
        } catch (error) {
            console.log(error);
            setErrorContent({'header': "User Registration Error", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            setIsLoading(false);
            return;
        }

        if(loginResponse.loginToken != null) {
            setLocalStorageEmail(formValues.emailId);
            setLocalStorageLoginToken(loginResponse.loginToken);
            setLocalStorageJwtToken(loginResponse.jwtToken);

            if (typeof android != "undefined"){
                android.showToast(formValues.emailId);
            }
            await callNativeApp(formValues.emailId);
            goToHome()
        } else {
            setErrorContent({'header': "Login Error", 'body': loginResponse.message});
            setIsSystemError(true);
            setIsLoading(false);
            return;
        }        
    }

    const loginAppleUser = useCallback( async (response) =>{
        setIsLoading(true);
        let appleLoginResponse;
        try {
            appleLoginResponse = await loginWithApple(response, countryName, countryCd, countryCurrCd, countryTelCd, location, userLatitude, userLongitude);
        } catch (error) {
            console.log(error);
            setErrorContent({'header': "Apple Login Problem", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            setIsLoading(false);
            return;
        }        
        
        setLocalStorageEmail(response.email);
        setLocalStorageLoginToken(appleLoginResponse.loginToken);
        setLocalStorageJwtToken(appleLoginResponse.jwtToken);

        await callNativeApp(response.email);
        goToHome()
    }, [countryName, countryCd, countryCurrCd, countryTelCd, location, userLatitude, userLongitude, goToHome])

    const loginGoogleUser = useCallback(async(email, name, profilePicUrl, googleLoginCaptcha) => {
        setIsLoading(true);

        let googleLoginResponse;
        try {
            googleLoginResponse = await loginWithGoogle(email, name, profilePicUrl, countryName, countryCd, countryCurrCd, countryTelCd, location, userLatitude, userLongitude, googleLoginCaptcha)
        } catch (error) {
            console.log(error);
            setErrorContent({'header': "Google Login Problem", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            setIsLoading(false);
            return;
        }
        
        setLocalStorageEmail(email);
        setLocalStorageLoginToken(googleLoginResponse.loginToken);
        setLocalStorageJwtToken(googleLoginResponse.jwtToken);
        setLocalStorageProfilePic(profilePicUrl);
        setProfilePicUrl(profilePicUrl);

        setUserFullName(name);

        if (typeof android != "undefined"){
            android.showToast(email);
        }
        await callNativeApp(email);
        
        if(googleLoginResponse.responseCode === 1){
            setIsNewFbGoogleUser(true);
        }
        getUserId();
    }, [countryName, countryCd, countryCurrCd, countryTelCd, location, userLatitude, userLongitude])    

    useEffect(() => {
        if (!userId) return;
        processUserId();        
    }, [userId, processUserId]);

    const initializeApple = async() => {    
        await AppleID.auth.init({
            clientId : 'com.stringssg.signin',
            scope : 'email name',
            redirectURI : 'https://www.stringssg.com',
            state : 'sign in',
            usePopup : true
        });
        setAppleReady(true);
    }   

    useEffect(() => {
        if(!isAppleReady) return
        document.addEventListener('AppleIDSignInOnSuccess', (data) => {
            if(data.detail.user != undefined){
                loginAppleUser(data.detail.user)
            } else {
                let appleResponse = parseJwt(data.detail.authorization.id_token);
                loginAppleUser(appleResponse);
            }
        });
        document.addEventListener('AppleIDSignInOnFailure', (error) => {
            console.log(error);
            setErrorContent({'header': "Sign in failed or cancelled by user", 'body': "Please try again."});
            setIsSystemError(true);        
        });
        
        setAppleReady(false)
    }, [isAppleReady, loginAppleUser])

    const handleCredentialResponse = useCallback( (response) => {
        const decodedResponse = parseJwt(response.credential);
        grecaptcha.enterprise.ready(async () => {
            const token = await grecaptcha.enterprise.execute(googleCaptchaKey, {action: 'GoogleLogin'});
            loginGoogleUser(decodedResponse.email, decodedResponse.name, decodedResponse.picture, token)
        });
    }, [loginGoogleUser])
    
    const initializeGoogleLogin = useCallback( async () => {
        if (typeof google === 'undefined' || !google?.accounts?.id) {
            setIsGoogleReady(false);
            return;
        }
        setIsGoogleBtnLoading(true);
        await google.accounts.id.initialize({
            client_id: "97723445335-nmgmbth2t3t6gqjr1jis91to3gj8vf1s.apps.googleusercontent.com",
            callback: handleCredentialResponse
        });
        await google.accounts.id.renderButton(
            document.getElementById("buttonDiv"),
            { theme: "outline", size: "large", width: "280", logo_alignment: "left", shape: "pill" }
        );
        setIsGoogleBtnLoading(false);
    }, [handleCredentialResponse])
    
    // Check if Google GSI is already loaded (client-side navigation)
    useEffect(() => {
        if (typeof window !== 'undefined' && window.google?.accounts?.id && !isGoogleReady) {
            setIsGoogleReady(true);
        }
    }, [isGoogleReady]);

    useEffect(() => {
        if(!isGoogleReady) return
        initializeGoogleLogin()
    }, [isGoogleReady, initializeGoogleLogin])

    useEffect(() => {
        if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream){
            if (/StringsSG\/[0-9\.]+$/.test(navigator.userAgent)){
                setIsApple(true);
            }          
        }
        window.loginGoogleUser = loginGoogleUser;
    }, [loginGoogleUser])

    // Mobile Login Handlers
    const handleMobileNumberChange = (e) => {        
        setMobileNumber(e.target.value)
        validateMobileNumber(e.target.value)
    }

    const validateMobileNumber = (value) =>{
        if(value.length < 8){
            setMobileNumberError("Mobile number should be atleast 8 digits");
        } else {
            setMobileNumberError("");
        }
    }

    const generateOneTimePin = async() => {
        setIsLoading(true);
        try{
            const response = await mobileLogin(loginTelCd, mobileNumber);
            if( response?.responseCode === 1 ){
                SetShowOtpForm(true);
            } else {
                setErrorContent({'header': "Mobile Not Registered", 'body': "Try to login with Email."});
                setIsSystemError(true);
            }
        } catch (error) {
            console.log(error)
            setErrorContent({'header': "Send OTP api error", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
        }
        setIsLoading(false);
    }

    const handleMobileNumberSumbit = (e) => { 
        e.preventDefault();
        generateOneTimePin();
    }
    
    const handleOtpChange = (e) => {
        setOtp(e.target.value);
    }

    const validateOtp = async() =>{
        setIsLoading(true);
        try {
            const loginResponse = await validateMobileLoginOTP(loginTelCd, mobileNumber, otp);
            if(loginResponse.loginToken != null) {
                setLocalStorageEmail(loginResponse.userEmail);
                setLocalStorageLoginToken(loginResponse.loginToken);
                setLocalStorageJwtToken(loginResponse.jwtToken);
    
                if (typeof android != "undefined"){
                    android.showToast(loginResponse.userEmail);
                }
                await callNativeApp(loginResponse.userEmail);
                goToHome()
            } else {
                setErrorContent({'header': "Login Error", 'body': loginResponse.message});
                setIsSystemError(true);
                setIsLoading(false);
            } 
        } catch (error) {
            console.log(error)
            setErrorContent({'header': "User Login Error", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            SetShowOtpForm(false);
            setOtp("")
            setIsLoading(false);
        }
    }

    const handleOtpSubmit = (e) => {
        e.preventDefault();
        validateOtp();        
    }

    // Dial code the mobile login starts with: the remembered country
    // (preferredMarket cookie), else the LocationContext one. The flag picker
    // overrides it, and the OTP calls send loginTelCd so that choice is used.
    useEffect(() => {
        if(loginTelCd) return
        const remembered = MARKETS[getRememberedMarket()]
        setLoginTelCd(remembered?.telCd ?? countryTelCd)
        setLoginCountryCd(remembered?.countryCd ?? countryCd)
    }, [countryTelCd, loginTelCd, countryCd])

    const openCountrySelection = () => {
        openModal(setIsShowCountrySelection)
    }

  return (
    <>   
        <Script id="android-google-login" strategy="lazyOnload">
            {`function androidGoogle(email, name, profilePicUrl){
                loginGoogleUser(email, name, profilePicUrl)
            }`}
        </Script>
        <Script 
            id="google-login-js"
            src="https://accounts.google.com/gsi/client"
            strategy="lazyOnload"
            onLoad={() => setIsGoogleReady(true)} 
        />
        <Script
            id="google-maps"
            src={`https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&language=en&libraries=places`}
        />
        <Script 
            id="apple-login-js"
            src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"
            strategy="lazyOnload"
            onLoad={() => initializeApple()} 
        />
        <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '128634041116395');
                fbq('track', 'PageView');`}
        </Script>
        <Script
            id="google-captcha"
            src={`https://www.google.com/recaptcha/enterprise.js?render=${googleCaptchaKey}`}
            strategy="lazyOnload"
        />
        
        {/* Full Screen Background */}
        <div className='fixed inset-0 bg-linear-to-br from-orange-500/10 via-amber-500/40 to-orange-600'>
            {/* Decorative circles */}
            <div className='absolute top-20 -left-20 w-60 h-60 bg-white/10 rounded-full blur-3xl' />
            <div className='absolute bottom-20 -right-20 w-80 h-80 bg-white/10 rounded-full blur-3xl' />
        </div>

        <div className='relative min-h-screen flex flex-col justify-center items-center px-4 py-8'>
            {/* Logo */}
            <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className='mb-6'
            >
                <Image 
                    src={s3Static + "logo.png"} 
                    alt="StringsSG" 
                    width={80} 
                    height={40}
                    style={{width: "80px", height: "100%"}}
                    className='drop-shadow-lg'
                    onError={(e) => { e.target.style.display = 'none' }}
                />
            </motion.div>

            {/* Main Card */}
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className='w-full max-w-sm bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden'
            >
                {/* Login Method Tabs */}
                <div className="p-4 pb-0">
                    <LoginMethodTabs 
                        isLoginWithEmail={isLoginWithEmail}
                        setIsLoginWithEmail={setIsLoginWithEmail}
                        emailFirst={true}
                    />
                </div>

                {/* Form Content */}
                <div className="p-4">
                    <AnimatePresence mode="wait">
                        {isLoginWithEmail ? (
                            <AnimatedFormContainer key="email" formKey="email" direction="left">
                                <EmailLoginForm
                                    onSubmit={handleSubmit}
                                    emailId={formValues.emailId}
                                    password={formValues.password}
                                    onEmailChange={handleChange}
                                    onPasswordChange={handleChange}
                                    passwordType={passwordType}
                                    onTogglePassword={() => setPasswordType(passwordType === "password" ? "text" : "password")}
                                    isLoading={isLoading}
                                />
                            </AnimatedFormContainer>
                        ) : (
                            <AnimatedFormContainer key="mobile" formKey="mobile" direction="right">
                                {!isShowOtpForm ? (
                                    <MobileNumberForm
                                        onSubmit={handleMobileNumberSumbit}
                                        mobileNumber={mobileNumber}
                                        onMobileNumberChange={handleMobileNumberChange}
                                        mobileNumberError={mobileNumberError}
                                        loginTelCd={loginTelCd}
                                        loginCountryCd={loginCountryCd}
                                        onCountrySelect={openCountrySelection}
                                        isLoading={isLoading}
                                    />
                                ) : (
                                    <OtpVerificationForm
                                        onSubmit={handleOtpSubmit}
                                        otp={otp}
                                        onOtpChange={handleOtpChange}
                                        loginTelCd={loginTelCd}
                                        mobileNumber={mobileNumber}
                                        isLoading={isLoading}
                                        onBack={() => { SetShowOtpForm(false); setOtp(""); }}
                                    />
                                )}
                            </AnimatedFormContainer>
                        )}
                    </AnimatePresence>
                </div>

                {/* Divider */}
                <div className="px-4 pb-3">
                    <OrDivider text="or" />
                </div>

                {/* Social Login */}
                <div className="px-4 pb-4 space-y-2">
                    <div className="flex justify-center items-center" style={{ minHeight: '44px' }}>
                        {isGoogleBtnLoading && (
                            <div style={{ width: '280px', height: '44px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <div className="w-5 h-5 border-2 border-gray-300 border-t-orange-500 rounded-full animate-spin" />
                            </div>
                        )}
                        <div id="buttonDiv" style={{ display: isGoogleBtnLoading ? 'none' : 'block' }}></div>
                    </div>
                    
                    {isApple && (
                        <div className='flex justify-center'>
                            <div id="appleid-signin" data-color="black" data-border="true" data-width="280" data-height="44" data-mode="center-align" data-type="sign in"></div>
                        </div>
                    )}
                </div>

                {/* Sign Up Link */}
                <div className="px-4 pb-6 text-center">
                    <Link href="/signup" className="text-sm text-orange-600 font-medium hover:text-orange-700">
                        Don't have an account? <span className="underline">Sign Up</span>
                    </Link>
                </div>
            </motion.div>
        </div>

        {isShowCountrySelection && (
            <AnimatePresence>
                <CountrySelection setLoginTelCd={setLoginTelCd} setLoginCountryCd={setLoginCountryCd} />
            </AnimatePresence>
        )}
        
        {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
        {isLoading && <LoadingCover/>}
    </>
  )
}
